import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import ReactCountryFlag from "react-country-flag";
import examples from "libphonenumber-js/examples.mobile.json";
import { AsYouType, getExampleNumber } from "libphonenumber-js";
import { toast } from "react-toastify";
import CustomDropdown from "../components/StatusDropdown";
import companyController from "../controllers/companyController";
import countryController from "../controllers/countryController";
import businessProfileController from "../controllers/businessProfileController";
import userController from "../controllers/userController";
import { userService } from "../services/userService";
import { initializeRegistrationSession } from "./registrationSession";
import { getPastryBusinessProfileIds } from "./pastryRegistration";
import PastryShopIcon from "../icons/registrationProcess/PastryShopIcon";
import Utils from "../utils/Utils";
import {
  BUSINESS_PROFILE_FALLBACKS,
  CUISINE_SECTIONS,
  DEFAULT_COUNTRIES,
  DELIVERY_FALLBACK_BUSINESS_PROFILE_CATEGORY_ID,
  DELIVERY_FALLBACK_BUSINESS_PROFILE_TYPE_ID,
  DELIVERY_TYPE_OPTIONS,
  GROCERY_CATEGORIES,
  cleanDocument,
  extractCompanyId,
  extractRegisteredUser,
  extractUserId,
  fileToPickedDocument,
  findBusinessProfileId,
  getErrorMessage,
  isApiError,
  isSuccessResponse,
  toBooleanLike,
} from "./registrationHelpers";
import {
  clearRegistrationDraft,
  mergeRegistrationDraft,
  readRegistrationDraft,
  saveRegistrationDraft,
} from "./registrationDraft";
import END_USER_TERMS_SECTIONS from "./endUserTerms.json";
import "../styles/RegistrationFlow.css";
import BackArrow from "../icons/registrationProcess/BackArrow";
import DeliveryIcon from "../icons/registrationProcess/DeliveryIcon";
import DropDownIcon from "../icons/registrationProcess/DropDownIcon";
import EmailIcon from "../icons/registrationProcess/EmailIcon";
import GetStartedIcon from "../icons/registrationProcess/GetStartedIcon";
import GlobalIcon from "../icons/registrationProcess/GlobalIcon";
import GroceryIcon from "../icons/registrationProcess/GroceryIcon";
import HidePassIcon from "../icons/registrationProcess/HidePassIcon";
import LegalEntityIcon from "../icons/registrationProcess/LegalEntityIcon";
import PasswordIcon from "../icons/registrationProcess/PasswordIcon";
import RegistrationFormIcon from "../icons/registrationProcess/RegistrationFormIcon";
import RegistrationVerfIcon from "../icons/registrationProcess/RegistrationVerfIcon";
import RestaurantIcon from "../icons/registrationProcess/RestaurantIcon";
import ShowPassIcon from "../icons/registrationProcess/ShowPassIcon";
import SmallGroceryIcon from "../icons/registrationProcess/SmallGroceryIcon";
import SmallRestaurantIcon from "../icons/registrationProcess/SmallRestaurantIcon";
import SuccessRegistrationIcon from "../icons/registrationProcess/SuccessRegistrationIcon";
import TermsAndConditionsIcon from "../icons/registrationProcess/TermsAndConditionsIcon";
import UploadIcon from "../icons/registrationProcess/UploadIcon";
import UserProfileIcon from "../icons/registrationProcess/UserProfileIcon";
import VendorIcon from "../icons/registrationProcess/VendorIcon";
import {
  translateLabel,
  translateText,
  useTranslationSnapshot,
  cleanRequiredLabel,
  translateFieldLabel,
} from "./../translations/runtime";

const OTP_LENGTH = 6;
const RESEND_INTERVAL = 60;
const MAX_RESEND_ATTEMPTS = 3;
const OTP_EMAIL_SUBJECT = "D2i2a w Noss OTP Verification";

const VENDOR_TERMS_SECTIONS = [
  {
    title: "1. Vendor Eligibility & Approval",
    body: [
      "To operate on the Platform, Vendors must be legally registered businesses, provide accurate business information, submit required legal documents, comply with applicable laws, and maintain accurate product listings.",
      "We may approve, reject, suspend, or remove any Vendor account at our sole discretion.",
    ],
  },
  {
    title: "2. Platform Model",
    body: [
      "D2I2A W NOSS is a marketplace platform, not a retailer for most products. Vendors are responsible for products they list, while the Platform provides technology, discovery, order coordination, customer service, and quality assurance.",
      "We may operate as a direct seller for select items, control marketplace presentation and visibility, and review or approve products before publishing them.",
    ],
  },
  {
    title: "3. Product Listing & Quality Control",
    body: [
      "Vendors must submit accurate product data before products go live. We may request edits, images, or other changes, and may reject or remove listings.",
      "Vendors are responsible for product accuracy, pricing correctness, image authenticity, legal compliance, and product safety. Products must match descriptions and images and must not be misleading or violate laws or IP rights.",
    ],
  },
  {
    title: "4. Store & Catalog Management",
    body: [
      "Vendors can create storefronts, add products, organize categories and sections, manage pricing and availability, and create bundles and offers.",
      "Vendors are responsible for correct inventory, real-time availability updates, and clear category structure. We may enforce category naming, catalog structure, and formatting standards.",
    ],
  },
  {
    title: "5. Orders & Fulfillment",
    body: [
      "Vendors are responsible for accepting and preparing orders, correct order fulfillment, and proper packaging.",
      "Delivery may be coordinated through independent drivers or delivery companies. Delivery execution after pickup and logistics delays are not the Vendor's responsibility.",
    ],
  },
  {
    title: "6. Promotions & Offers",
    body: [
      "Vendors may create discounts, BOGO offers, bundles, meal deals, category-based promotions, and family bundles where applicable.",
      "We may approve, reject, limit visibility, limit duration, or remove misleading promotions. Only one promotion may apply per order unless platform rules define otherwise.",
    ],
  },
  {
    title: "7. Featured & Sponsored Placements",
    body: [
      "We may offer paid visibility services such as featured placements, sponsored listings, homepage exposure, category pages, search results, and vendor discovery surfaces.",
      "Placement may depend on campaign type, category relevance, budget or bidding, performance scoring, and consumer algorithm factors. We may adjust ranking logic, limit abusive or low-quality campaigns, and remove misleading sponsored content.",
    ],
  },
  {
    title: "8. Fees & Commissions",
    body: [
      "Vendors agree to pay platform commission per order and optional promotional or featured placement fees. Fees may vary by category, campaign type, or vendor agreement and may be updated with notice through the Platform.",
    ],
  },
  {
    title: "9. Store Management & Employees",
    body: [
      "Each Vendor account includes one free authorized user account. Additional employee or staff accounts are charged at USD $6 per additional authorized user per month.",
      "Vendors are responsible for employee actions, account security, permission management, and misuse of credentials. We may suspend accounts, revoke access, or audit user activity for compliance.",
    ],
  },
  {
    title: "10. Prohibited Conduct",
    body: [
      "Vendors must not sell illegal or counterfeit products, provide false product information, manipulate ratings or reviews, abuse promotions or refunds, bypass platform fees, interfere with platform systems, or mislead customers.",
      "Violations may result in product removal, account suspension, or permanent termination.",
    ],
  },
  {
    title: "11. Payments & Settlements",
    body: [
      "We process customer payments and transfer Vendor earnings after deductions such as commissions, refunds, promotional costs, and platform fees.",
      "Settlement timing depends on operational cycles, payment provider rules, and risk checks. Payments may be delayed for fraud suspicion, customer disputes, or policy violations.",
    ],
  },
  {
    title: "12. Refunds & Disputes",
    body: [
      "Refunds may be issued for wrong items, missing items, quality issues, or delivery issues. We may investigate disputes, deduct amounts from Vendor balance, and approve or reject claims. Vendor may be responsible for refund costs if fault is confirmed.",
    ],
  },
  {
    title: "13. Intellectual Property",
    body: [
      "Vendors retain ownership of logos, brand assets, and product content. By using the Platform, Vendors grant us rights to display products, use images for marketing and listing, and promote products within the marketplace.",
    ],
  },
  {
    title: "14. Confidentiality",
    body: [
      "Vendors must not copy platform systems, reverse engineer tools, misuse internal platform logic, or share confidential operational insights. All platform systems remain property of D2I2A W NOSS / Javelynx.",
    ],
  },
  {
    title: "15. Data & Privacy",
    body: [
      "We may collect business data, product data, order performance, and customer interaction data for platform operations, analytics, fraud prevention, and service improvement. We do not sell vendor data.",
    ],
  },
  {
    title: "16. Suspension & Termination",
    body: [
      "We may suspend or terminate Vendors if policies are violated, fraud is detected, performance is unacceptable, or customer complaints are excessive. We may remove specific products or fully terminate accounts.",
    ],
  },
  {
    title: "17. Liability Limitation",
    body: [
      "We are not liable for loss of revenue, operational disruptions, delivery issues, customer misuse, or third-party failures. Vendors operate at their own business risk.",
    ],
  },
  {
    title: "18. Changes To Terms",
    body: [
      "We may update these Terms at any time. Continued use of the Platform means acceptance of updates.",
    ],
  },
  {
    title: "19. Governing Law",
    body: [
      "These Terms follow the laws of the jurisdiction where D2I2A W NOSS operates.",
    ],
  },
];

const DELIVERY_TERMS_SECTIONS = [
  {
    title: "Terms and Conditions",
    body: [
      "By creating an account, you agree to use D2i2a w Noss services responsibly, provide accurate information, and follow platform rules.",
      "You also agree that D2i2a w Noss may process your registration information for account creation, verification, and platform operations.",
    ],
  },
];

const stepMeta = {
  "/register": { active: 1, total: 11 },
  "/register/business-type": { active: 2, total: 11 },
  "/register/vendor/category": { active: 3, total: 11 },
  "/register/vendor/account": { active: 4, total: 11 },
  "/register/vendor/terms": { active: 5, total: 11 },
  "/register/vendor/verify": { active: 6, total: 11 },
  "/register/vendor/documents": { active: 7, total: 11 },
  "/register/documents/terms": { active: 8, total: 11 },
  "/register/address": { active: 9, total: 11 },
  "/register/success": { active: 10, total: 11 },
  "/register/delivery/account": { active: 2, total: 9 },
  "/register/delivery/terms": { active: 3, total: 9 },
  "/register/delivery/verify": { active: 4, total: 9 },
  "/register/delivery/type": { active: 5, total: 9 },
  "/register/delivery/documents": { active: 6, total: 9 },
  "/register/delivery/address": { active: 7, total: 9 },
  "/register/delivery/success": { active: 8, total: 9 },
};

const normalizeCountries = (response) => {
  const data =
    (Array.isArray(response?.data) && response.data) ||
    (Array.isArray(response?.data?.countries) && response.data.countries) ||
    (Array.isArray(response?.data?.data) && response.data.data) ||
    [];

  const countries = data
    .map((country) => {
      const code = String(country?.countryCode || "").toUpperCase();
      const label = String(country?.countryName || "").trim();
      const suffixRaw = String(country?.suffix || "").trim();
      if (!code || !label || !suffixRaw) return null;

      return {
        code,
        label,
        dialCode: suffixRaw.startsWith("+") ? suffixRaw : `+${suffixRaw}`,
      };
    })
    .filter(Boolean);

  return countries.length ? countries : DEFAULT_COUNTRIES;
};

const useDraft = () => {
  const location = useLocation();
  return useMemo(() => {
    const stateDraft = location.state?.draft || {};
    if (Object.keys(stateDraft).length) {
      saveRegistrationDraft(stateDraft);
      return stateDraft;
    }
    return readRegistrationDraft();
  }, [location.state]);
};

const useDraftNavigate = () => {
  const navigate = useNavigate();
  return (path, partialDraft = {}) => {
    const draft = mergeRegistrationDraft(partialDraft);
    navigate(path, { state: { draft } });
  };
};

export const RegistrationLayout = ({
  children,
  title,
  subtitle,
  icon,
  className = "",
  showBack = true,
}) => {
  useTranslationSnapshot();
  const navigate = useNavigate();
  const location = useLocation();
  const meta = stepMeta[location.pathname] || { active: 1, total: 10 };

  return (
    <main className={`registration-page ${className}`}>
      <section className="registration-shell">
        {showBack && (
          <button
            type="button"
            className="registration-back"
            aria-label={translateLabel("txt_end_user_go_back", "Go back")}
            onClick={() => navigate(-1)}
          >
            <BackArrow />
          </button>
        )}

        <div className="registration-top">
          <div className="registration-hero-icon">{icon}</div>
          <div className="registration-dots" aria-hidden="true">
            {Array.from({ length: meta.total }).map((_, index) => (
              <span
                key={index}
                className={index === meta.active ? "is-active" : ""}
              />
            ))}
          </div>
        </div>

        <div className="registration-panel">
          {title ? <h1>{translateText(title)}</h1> : null}
          {subtitle ? (
            <p className="registration-subtitle">{translateText(subtitle)}</p>
          ) : null}
          {children}
        </div>
      </section>
    </main>
  );
};

export const FooterTerms = () => {
  useTranslationSnapshot();
  return (
    <p className="registration-terms">
      {" "}
      {translateLabel(
        "txt_account_agreement",
        "By creating an account, you agree to D2i2a w Noss",
      )}{" "}
      <Link to="/privacy">
        {translateLabel("lnk_privacy_policy", "Privacy Policy")}
      </Link>
      .
    </p>
  );
};

const PasswordRules = ({ checks }) => {
  useTranslationSnapshot();
  const rules = [
    ["At least 8 characters", checks.length],
    ["At least 1 uppercase letter", checks.uppercase],
    ["At least 1 lowercase letter", checks.lowercase],
    ["At least 1 number", checks.digit],
    ["At least 1 special character", checks.special],
  ];

  return (
    <div className="registration-password-rules">
      {rules.map(([label, valid]) => (
        <div key={label} className={valid ? "is-valid" : ""}>
          <span>{valid ? "✓" : null}</span>
          {translateText(label)}
        </div>
      ))}
    </div>
  );
};

const CountryPicker = ({
  countries,
  selectedCountry,
  onSelect,
  showDialCode = false,
  buttonLabel,
}) => {
  useTranslationSnapshot();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const pickerRef = useRef(null);
  const filteredCountries = useMemo(() => {
    const term = search.toLowerCase().trim();
    const digits = search.replace(/\D/g, "");
    if (!term && !digits) return countries;

    return countries.filter(
      (country) =>
        country.label.toLowerCase().includes(term) ||
        country.code.toLowerCase().includes(term) ||
        (digits && country.dialCode.replace("+", "").includes(digits)),
    );
  }, [countries, search]);

  useEffect(() => {
    if (!open) return undefined;

    const handleOutsideClick = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setOpen(false);
        setSearch("");
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", handleOutsideClick, true);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick, true);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  return (
    <div
      ref={pickerRef}
      className={`registration-country-picker ${
        showDialCode ? "is-compact" : ""
      } ${open ? "is-open" : ""}`.trim()}
    >
      <button
        type="button"
        className="registration-select-button"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((current) => !current)}
      >
        {selectedCountry ? (
          <ReactCountryFlag
            countryCode={selectedCountry.code}
            svg
            className="registration-country-flag"
          />
        ) : (
          <GlobalIcon size={18} />
        )}
        <span className="registration-select-label">
          {buttonLabel ||
            selectedCountry?.label ||
            translateLabel("txt_end_user_select_country", "Select country")}
        </span>
        <DropDownIcon />
      </button>

      {open ? (
        <div className="registration-country-menu">
          <input
            className="registration-country-search"
            type="search"
            placeholder={translateLabel(
              "txt_end_user_search_country",
              "Search country",
            )}
            value={search}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => setSearch(event.target.value)}
          />
          <div className="registration-country-list" role="listbox">
            {filteredCountries.map((country) => (
              <button
                type="button"
                key={`${country.code}-${country.dialCode}`}
                role="option"
                aria-selected={country.label === selectedCountry?.label}
                className={
                  country.label === selectedCountry?.label ? "is-selected" : ""
                }
                onClick={() => {
                  onSelect(country);
                  setOpen(false);
                  setSearch("");
                }}
              >
                <ReactCountryFlag countryCode={country.code} svg />
                <span>{translateText(country.label)}</span>
                {showDialCode ? <small>{country.dialCode}</small> : null}
              </button>
            ))}
            {!filteredCountries.length ? (
              <div className="registration-empty">
                {translateLabel(
                  "txt_end_user_no_countries_found",
                  "No countries found",
                )}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export const TextField = ({
  label,
  icon,
  required = true,
  error,
  children,
}) => {
  useTranslationSnapshot();
  return (
    <label className={`registration-field ${error ? "has-error" : ""}`}>
      <span>
        {cleanRequiredLabel(translateText(label))}
        {required ? <i>*</i> : null}
      </span>
      <div className="registration-input-wrap">
        {icon ? <span className="registration-input-icon">{icon}</span> : null}
        {children}
      </div>
      {error ? <small>{translateText(error)}</small> : null}
    </label>
  );
};

const RegistrationDropdown = ({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
}) => {
  useTranslationSnapshot();
  return (
    <CustomDropdown
      value={value}
      onChange={onChange}
      options={options}
      placeholder={translateText(placeholder)}
      disabled={disabled}
      className="registration-dropdown"
      openClassName="is-open"
      menuClassName="registration-dropdown-menu"
      optionClassName="registration-dropdown-option"
      renderTrigger={({
        selectedLabel,
        hasSelection,
        placeholder: triggerPlaceholder,
      }) => (
        <div
          className={`registration-dropdown-trigger ${
            hasSelection ? "has-value" : ""
          }`.trim()}
        >
          <span>{selectedLabel ? translateText(selectedLabel) : triggerPlaceholder}</span>
          <DropDownIcon />
        </div>
      )}
    />
  );
};

const useCountries = () => {
  const [countries, setCountries] = useState(DEFAULT_COUNTRIES);

  useEffect(() => {
    let cancelled = false;
    const loadCountries = async () => {
      try {
        const response = await countryController.getCountries();
        if (!cancelled) setCountries(normalizeCountries(response));
      } catch {
        if (!cancelled) setCountries(DEFAULT_COUNTRIES);
      }
    };
    loadCountries();
    return () => {
      cancelled = true;
    };
  }, []);

  return countries;
};

const getPhonePlaceholder = (country) => {
  try {
    const example = getExampleNumber(country.code, examples);
    return example ? example.formatNational() : "Phone Number";
  } catch {
    return "Phone Number";
  }
};

const resolveBusinessProfileIds = async ({
  jwt,
  businessTypeLabel,
  businessProfileTypeId,
  businessProfileCategoryId,
  categoryLabel,
}) => {
  const fallback = {
    businessProfileTypeId,
    businessProfileCategoryId,
  };

  if (!jwt) return fallback;

  try {
    const response =
      await businessProfileController.getBusinessProfileTypesAndCategories({
        jwt,
      });
    if (response?.status !== 200) return fallback;

    return {
      businessProfileTypeId:
        findBusinessProfileId(
          response?.data?.businessProfileTypes,
          "businessProfileTypeId",
          businessTypeLabel,
        ) || businessProfileTypeId,
      businessProfileCategoryId:
        findBusinessProfileId(
          response?.data?.businessProfileCategories,
          "businessProfileCategoryId",
          categoryLabel || businessTypeLabel,
        ) ||
        findBusinessProfileId(
          response?.data?.businessProfileCategories,
          "businessProfileCategoryId",
          businessTypeLabel,
        ) ||
        businessProfileCategoryId,
    };
  } catch {
    return fallback;
  }
};

const isPhoneAlreadyInUse = (data) =>
  data === true ||
  data?.exists === true ||
  data?.inUse === true ||
  data?.phoneInUse === true;

const REGISTRATION_VALIDATION_MESSAGES = {
  companyNameRequired: "Company name is required.",
  driverNameRequired: "Driver name is required.",
  taxNumberInvalid: "Tax Number must contain numbers only.",
  companyDocumentsRequired: "Please upload the required company documents.",
  driverDocumentsRequired: "Please upload the required driver documents.",
};

const sanitizeRegistrationDebugPayload = (payload = {}) => ({
  ...payload,
  password: payload.password ? "[password omitted]" : payload.password,
});

const RegistrationAccountForm = ({ mode }) => {
  useTranslationSnapshot();
  const navigateWithDraft = useDraftNavigate();
  const draft = useDraft();
  const countries = useCountries();
  const isDelivery = mode === "delivery";
  const selectedMeta = draft.businessType || {
    label: "Other",
    categoryLabel: "Other",
    ...BUSINESS_PROFILE_FALLBACKS.Other,
  };
  const isPastry = !isDelivery && selectedMeta.label === "Pastry";
  const [form, setForm] = useState({
    firstName: draft.registrationDTO?.firstname || "",
    lastName: draft.registrationDTO?.lastname || "",
    countryOfResidence: draft.registrationDTO?.countryOfResidence || "Lebanon",
    email: draft.registrationDTO?.email || "",
    phone: draft.registrationDTO?.phoneNumber || "",
    password: draft.registrationDTO?.password || "",
    businessDescription:
      draft.registrationDTO?.businessDescription ||
      selectedMeta.businessDescription ||
      "",
  });
  const [selectedCountry, setSelectedCountry] = useState(
    countries.find((country) => country.code === "LB") || countries[0],
  );
  const [showPassword, setShowPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const nextCountry =
      countries.find((country) => country.code === "LB") || countries[0];
    setSelectedCountry(nextCountry);
    setForm((current) => ({
      ...current,
      countryOfResidence:
        current.countryOfResidence || nextCountry?.label || "",
    }));
  }, [countries]);

  const passwordChecks = useMemo(
    () => Utils.checkPasswordFormat(form.password || ""),
    [form.password],
  );
  const email = form.email.trim().toLowerCase();
  const phoneDigits = String(form.phone || "").replace(/[^\d]/g, "");
  const isEmailValid = Utils.checkEmailFormat(email);
  const canSubmit =
    Utils.checkName(form.firstName.trim()) &&
    Utils.checkName(form.lastName.trim()) &&
    form.countryOfResidence.trim() &&
    isEmailValid &&
    phoneDigits &&
    passwordChecks.allValid;

  const update = (field) => (event) => {
    const value =
      field === "firstName" || field === "lastName"
        ? Utils.sanitizeNameInput(event.target.value)
        : event.target.value;

    setForm((current) => ({ ...current, [field]: value }));
  };

  const handlePhoneChange = (event) => {
    const rawValue = event.target.value;
    try {
      setForm((current) => ({
        ...current,
        phone: new AsYouType(selectedCountry.code).input(rawValue),
      }));
    } catch {
      setForm((current) => ({
        ...current,
        phone: rawValue.replace(/[^\d+]/g, ""),
      }));
    }
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    const formatted = new AsYouType(country.code).input(phoneDigits);
    setForm((current) => ({ ...current, phone: formatted }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit || loading) {
      toast.error(
        translateLabel(
          "txt_end_user_please_fill_all_required_fields_correctly",
          "Please fill all required fields correctly.",
        ),
      );
      return;
    }

    const suffix = Number((selectedCountry.dialCode || "").replace("+", ""));
    const phoneNumber = Number(phoneDigits);
    if (!Number.isFinite(suffix) || !Number.isFinite(phoneNumber)) {
      toast.error(
        translateLabel(
          "txt_end_user_invalid_phone_number",
          "Invalid phone number.",
        ),
      );
      return;
    }

    setLoading(true);
    try {
      const emailCheck = await userController.checkIfEmailInUse({ email });
      if (toBooleanLike(emailCheck?.data) === true) {
        toast.error(
          translateLabel(
            "txt_common_this_email_address_is_already_in_use",
            "This email address is already in use.",
          ),
        );
        return;
      }
      if (emailCheck?.status !== 200) {
        toast.error(
          translateLabel(
            "txt_common_failed_to_verify_email_please_try_again",
            "Failed to verify email. Please try again.",
          ),
        );
        return;
      }

      const phoneCheck = await userController.checkExistsPhoneNumber({
        phoneNumber,
      });
      if (isPhoneAlreadyInUse(phoneCheck?.data)) {
        toast.error(
          translateLabel(
            "txt_common_this_phone_number_is_already_in_use",
            "This phone number is already in use.",
          ),
        );
        return;
      }
      if (phoneCheck?.status !== 200) {
        toast.error(
          translateLabel(
            "txt_common_failed_to_verify_phone_please_try_again",
            "Failed to verify phone. Please try again.",
          ),
        );
        return;
      }

      const profileIds = isDelivery
        ? {
            businessProfileTypeId: DELIVERY_FALLBACK_BUSINESS_PROFILE_TYPE_ID,
            businessProfileCategoryId:
              DELIVERY_FALLBACK_BUSINESS_PROFILE_CATEGORY_ID,
          }
        : isPastry
          ? await getPastryBusinessProfileIds()
          : await resolveBusinessProfileIds({
              jwt: userService.getJwt(),
              businessTypeLabel: selectedMeta.label,
              categoryLabel: selectedMeta.categoryLabel,
              businessProfileTypeId: selectedMeta.typeId,
              businessProfileCategoryId: selectedMeta.categoryId,
            });

      const registrationDTO = {
        businessDescription: isDelivery
          ? "Delivery driver"
          : (isPastry
              ? form.businessDescription.trim()
              : selectedMeta.businessDescription) ||
            `${selectedMeta.label} business account.`,
        businessType: isDelivery
          ? "DELIVERY"
          : isPastry
            ? "PATISSERIE"
            : selectedMeta.label === "Restaurant"
              ? "RESTAURANTS"
              : "GROCERIES",
        category: isDelivery
          ? "DELIVERY"
          : isPastry
            ? "PATISSERIE"
            : selectedMeta.label === "Restaurant"
              ? "RESTAURANTS"
              : "GROCERIES",
        businessName:
          [
            form.firstName.trim(),
            form.lastName.trim(),
            isDelivery ? "" : selectedMeta.label,
          ]
            .filter(Boolean)
            .join(" ") || email,
        businessProfileCategoryId: profileIds.businessProfileCategoryId,
        businessProfileTypeId: profileIds.businessProfileTypeId,
        ...(isDelivery
          ? {
              businessProfileID: profileIds.businessProfileTypeId,
              businessProfileId: profileIds.businessProfileTypeId,
            }
          : {}),
        countryOfResidence: form.countryOfResidence.trim(),
        cuisineType: isDelivery ? "" : selectedMeta.cuisineType || "",
        email,
        firstname: form.firstName.trim(),
        isTermsAndConditionsAccepted: false,
        lastname: form.lastName.trim(),
        password: form.password,
        phoneNumber,
        suffix,
        userType: isDelivery ? "INDIVIDUAL_DRIVER" : "VENDOR",
      };

      navigateWithDraft(
        isDelivery ? "/register/delivery/terms" : "/register/vendor/terms",
        {
          flow: isDelivery ? "delivery" : "vendor",
          ...(isPastry
            ? {
                businessType: {
                  ...selectedMeta,
                  businessDescription: form.businessDescription.trim(),
                },
              }
            : {}),
          registrationDTO,
          otpEmailSubject: OTP_EMAIL_SUBJECT,
        },
      );
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to continue registration."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <RegistrationLayout
      title={translateLabel("txt_create_account", "Create your account")}
      icon={<RegistrationFormIcon />}
      className="registration-account-page"
    >
      <form className="registration-form" onSubmit={handleSubmit}>
        <div className="registration-two-col">
          <TextField
            label={translateLabel("input_first_name", "First Name")}
            icon={<UserProfileIcon size={21.78} />}
          >
            <input
              value={form.firstName}
              onChange={update("firstName")}
              autoComplete="given-name"
            />
          </TextField>
          <TextField
            label={translateLabel("input_last_name", "Last Name")}
            icon={<UserProfileIcon size={21.78} />}
          >
            <input
              value={form.lastName}
              onChange={update("lastName")}
              autoComplete="family-name"
            />
          </TextField>
        </div>

        <div className="registration-field">
          <span>
            {" "}
            {translateFieldLabel(
              "input_country_of_residence",
              "Country of Residence",
            )}
            <i>*</i>
          </span>
          <CountryPicker
            countries={countries}
            selectedCountry={
              countries.find(
                (country) => country.label === form.countryOfResidence,
              ) || selectedCountry
            }
            onSelect={(country) =>
              setForm((current) => ({
                ...current,
                countryOfResidence: country.label,
              }))
            }
          />
        </div>

        <TextField
          label={translateLabel("input_email_address", "Email address")}
          icon={<EmailIcon size={21.78} />}
          error={
            form.email.trim() && !isEmailValid
              ? "Please enter a valid email address"
              : ""
          }
        >
          <input
            type="email"
            value={form.email}
            onChange={update("email")}
            autoComplete="email"
          />
        </TextField>

        <TextField
          label={translateLabel("input_phone_number", "Phone Number")}
          icon={null}
        >
          <div className="registration-phone">
            <CountryPicker
              countries={countries}
              selectedCountry={selectedCountry}
              onSelect={handleCountrySelect}
              showDialCode
              buttonLabel={selectedCountry?.dialCode}
            />
            <input
              type="tel"
              value={form.phone}
              onChange={handlePhoneChange}
              placeholder={getPhonePlaceholder(selectedCountry)}
              autoComplete="tel-national"
            />
          </div>
        </TextField>

        <TextField
          label={translateLabel("input_password", "Password")}
          icon={null}
        >
          <PasswordIcon size={21.78} />
          <input
            type={showPassword ? "text" : "password"}
            value={form.password}
            onChange={update("password")}
            onFocus={() => setPasswordFocused(true)}
            onBlur={() => setPasswordFocused(false)}
            autoComplete="new-password"
          />
          <button
            type="button"
            className="registration-eye"
            aria-label={translateText(
              showPassword ? "Hide password" : "Show password",
            )}
            onClick={() => setShowPassword((value) => !value)}
          >
            {showPassword ? <HidePassIcon /> : <ShowPassIcon />}
          </button>
        </TextField>
        {passwordFocused && form.password ? (
          <PasswordRules checks={passwordChecks} />
        ) : null}

        {isPastry && (
          <label className="registration-field">
            <span>
              {translateLabel("input_business_description", "Description")}
            </span>
            <textarea
              value={form.businessDescription}
              onChange={update("businessDescription")}
              maxLength={1000}
              placeholder={translateLabel(
                "input_business_description_placeholder",
                "Describe your business",
              )}
            />
          </label>
        )}

        <button
          type="submit"
          className="registration-primary"
          disabled={loading || !canSubmit}
        >
          {loading
            ? translateLabel("txt_common_please_wait", "Please wait...")
            : translateLabel("btn_save_continue", "Save & Continue")}
        </button>
      </form>

      <FooterTerms />
    </RegistrationLayout>
  );
};

export const RegistrationStart = () => {
  useTranslationSnapshot();
  const navigateWithDraft = useDraftNavigate();

  useEffect(() => {
    clearRegistrationDraft();
  }, []);

  return (
    <RegistrationLayout
      title={translateLabel(
        "txt_select_business_type",
        "Select Your Business Type",
      )}
      icon={<GetStartedIcon />}
    >
      <div className="registration-choice-grid is-large">
        <button
          type="button"
          className="registration-choice"
          onClick={() =>
            navigateWithDraft("/register/business-type", { flow: "vendor" })
          }
        >
          <VendorIcon size={76} />
          <strong>{translateLabel("btn_vendor", "Vendor")}</strong>
          <span>
            {translateLabel(
              "txt_vendor_description",
              "Register your business now and reach thousands.",
            )}
          </span>
        </button>
        <button
          type="button"
          className="registration-choice"
          onClick={() =>
            navigateWithDraft("/register/delivery/account", {
              flow: "delivery",
            })
          }
        >
          <DeliveryIcon size={76} />
          <strong>{translateLabel("btn_delivery", "Delivery")}</strong>
          <span>
            {translateLabel(
              "txt_delivery_description",
              "Start delivering today and turn miles into money.",
            )}
          </span>
        </button>
      </div>
      <Link className="registration-text-link" to="/sign-in">
        {" "}
        {translateLabel("txt_sign_in", "Sign In")}{" "}
      </Link>
    </RegistrationLayout>
  );
};

export const BusinessTypeSelection = () => {
  useTranslationSnapshot();
  const navigateWithDraft = useDraftNavigate();
  const cards = [
    {
      label: "Restaurant",
      path: "/register/vendor/category",
      icon: <RestaurantIcon size={76} />,
    },
    {
      label: "Grocery",
      path: "/register/vendor/category",
      icon: <GroceryIcon size={76} />,
    },
    {
      label: "Pastry",
      path: "/register/vendor/account",
      icon: <PastryShopIcon />,
    },
  ];

  return (
    <RegistrationLayout
      title={translateLabel(
        "txt_select_business_type",
        "Select Your Business Type",
      )}
      icon={<GetStartedIcon />}
    >
      <div className="registration-choice-grid">
        {cards.map((card) => (
          <button
            type="button"
            key={card.label}
            className="registration-choice is-square"
            onClick={() =>
              navigateWithDraft(card.path, {
                flow: "vendor",
                businessType: {
                  label: card.label,
                  categoryLabel: card.label,
                  typeId: BUSINESS_PROFILE_FALLBACKS[card.label]?.typeId,
                  categoryId:
                    BUSINESS_PROFILE_FALLBACKS[card.label]?.categoryId,
                },
              })
            }
          >
            {card.icon}
            <strong>{translateText(card.label)}</strong>
          </button>
        ))}
      </div>
    </RegistrationLayout>
  );
};

export const VendorCategoryDetails = () => {
  useTranslationSnapshot();
  const draft = useDraft();
  const navigateWithDraft = useDraftNavigate();
  const selectedType = draft.businessType?.label || "Other";
  const [description, setDescription] = useState(
    draft.businessType?.businessDescription || "",
  );
  const [groceryCategory, setGroceryCategory] = useState(
    draft.businessType?.categoryLabel !== selectedType
      ? draft.businessType?.categoryLabel || ""
      : "",
  );
  const [selectedCuisineIds, setSelectedCuisineIds] = useState([]);
  const cuisineLabels = CUISINE_SECTIONS.flatMap(
    (section) => section.items,
  ).reduce((acc, item) => ({ ...acc, [item.id]: item.label }), {});

  if (!draft.businessType)
    return <Navigate to="/register/business-type" replace />;

  const selectedCuisineSummary = selectedCuisineIds
    .map((id) => cuisineLabels[id])
    .filter(Boolean)
    .slice(0, 2)
    .join(", ");
  const selectedCuisineType = selectedCuisineIds
    .map((id) => cuisineLabels[id])
    .filter(Boolean)
    .join(" | ");

  const handleContinue = () => {
    const categoryLabel =
      selectedType === "Grocery" ? groceryCategory || "Grocery" : selectedType;
    const nextDescription =
      description.trim() ||
      (selectedType === "Restaurant" && selectedCuisineSummary
        ? `Restaurant business focused on ${selectedCuisineSummary}.`
        : `${selectedType} business account.`);

    navigateWithDraft("/register/vendor/account", {
      businessType: {
        ...draft.businessType,
        categoryLabel,
        businessDescription: nextDescription,
        cuisineType:
          selectedType === "Restaurant"
            ? selectedCuisineType
            : selectedType === "Grocery"
              ? categoryLabel
              : "",
      },
    });
  };

  return (
    <RegistrationLayout
      title={translateLabel("txt_create_account", "Create your Account")}
      icon={
        <span className="registration-category-hero-icon">
          {selectedType === "Grocery" ? (
            <GroceryIcon size={86} />
          ) : (
            <RestaurantIcon size={86} />
          )}
        </span>
      }
    >
      <div className="registration-form">
        <div className="registration-readonly-row">
          <span>
            {" "}
            {translateFieldLabel("input_business_type", "Business Type")}
            <i>*</i>
          </span>
          <div>
            {selectedType === "Grocery" ? (
              <SmallGroceryIcon size={22} />
            ) : (
              <SmallRestaurantIcon size={22} />
            )}
            <strong>{translateText(selectedType)}</strong>
          </div>
        </div>

        {selectedType === "Restaurant" ? (
          <div className="registration-section-list">
            <span>{translateLabel("input_cuisine_type", "Cuisine Type")}</span>
            {CUISINE_SECTIONS.map((section) => (
              <details key={section.id} open>
                <summary>{translateText(section.title)}</summary>
                {section.items.map((item) => (
                  <label key={item.id} className="registration-check-row">
                    <input
                      type="checkbox"
                      checked={selectedCuisineIds.includes(item.id)}
                      onChange={() =>
                        setSelectedCuisineIds((current) =>
                          current.includes(item.id)
                            ? current.filter((id) => id !== item.id)
                            : [...current, item.id],
                        )
                      }
                    />
                    <span>{translateText(item.label)}</span>
                    <small>{item.badge}</small>
                  </label>
                ))}
              </details>
            ))}
          </div>
        ) : null}

        {selectedType === "Grocery" ? (
          <label className="registration-field">
            <span>
              {translateLabel(
                "txt_common_grocery_category",
                "Grocery Category",
              )}
            </span>
            <RegistrationDropdown
              value={groceryCategory}
              onChange={setGroceryCategory}
              placeholder={translateLabel("txt_common_grocery", "Grocery")}
              options={[
                { label: "Grocery", value: "" },
                ...GROCERY_CATEGORIES.map((category) => ({
                  label: category,
                  value: category,
                })),
              ]}
            />
          </label>
        ) : null}

        <label className="registration-field">
          <span>
            {" "}
            {translateLabel("input_business_description", "Description")}{" "}
          </span>
          <textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            maxLength={1000}
            placeholder={translateLabel(
              "input_business_description_placeholder",
              "Describe your business",
            )}
          />
          <em className="registration-character-counter">
            ({1000 - description.length}{" "}
            {translateLabel("txt_common_characters", "characters)")}
          </em>
        </label>

        <button
          type="button"
          className="registration-primary"
          onClick={handleContinue}
        >
          {" "}
          {translateLabel("btn_save_continue", "Save & continue")}{" "}
        </button>
      </div>
      <FooterTerms />
    </RegistrationLayout>
  );
};

const TermsStep = ({ mode, documents = false }) => {
  useTranslationSnapshot();
  const navigate = useNavigate();
  const draft = useDraft();
  const navigateWithDraft = useDraftNavigate();
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const isVendorTerms = documents && mode === "vendor";
  const termsSections = !documents
    ? END_USER_TERMS_SECTIONS
    : isVendorTerms
      ? VENDOR_TERMS_SECTIONS
      : DELIVERY_TERMS_SECTIONS;
  const registrationDTO = draft.registrationDTO || {};

  if (!registrationDTO.email) {
    return (
      <Navigate
        to={
          mode === "delivery"
            ? "/register/delivery/account"
            : "/register/vendor/account"
        }
        replace
      />
    );
  }

  const handleRegisterUser = async () => {
    const payload = {
      ...registrationDTO,
      isTermsAndConditionsAccepted: true,
      userType:
        String(registrationDTO.userType || "").toUpperCase() ===
        "INDIVIDUAL_DRIVER"
          ? "INDIVIDUAL_DRIVER"
          : registrationDTO.userType,
    };

    const response = await userController.register({
      userRegisterRequest: payload,
      isAdmin: true,
    });
    if (isApiError(response) || !isSuccessResponse(response)) {
      throw response;
    }

    const registeredUser = extractRegisteredUser(response);
    const registeredUserId = extractUserId(registeredUser);
    const businessProfileId = String(
      registeredUser?.businessProfileId ||
        registeredUser?.businessProfileID ||
        payload.businessProfileID ||
        payload.businessProfileId ||
        payload.businessProfileTypeId ||
        payload.businessProfileCategoryId ||
        "",
    ).trim();

    const nextPayload = {
      ...payload,
      ...(registeredUserId
        ? {
            id: registeredUserId,
            userID: registeredUserId,
            userId: registeredUserId,
          }
        : {}),
      ...(businessProfileId
        ? { businessProfileID: businessProfileId, businessProfileId }
        : {}),
      ...(registeredUser?.userName
        ? { userName: registeredUser.userName }
        : {}),
      ...(registeredUser?.creationDate
        ? { creationDate: registeredUser.creationDate }
        : {}),
    };

    const authResponse = await userController.authenticate({
      authenticateRequest: {
        username: nextPayload.email,
        password: nextPayload.password,
      },
      authenticationPortalType: "ADMIN_PORTAL",
    });
    if (isApiError(authResponse) || authResponse?.status !== 200) {
      throw new Error("Auto-login failed. Please try again.");
    }

    const activationJwt = userService.getTokenFromResponse(authResponse);
    if (!activationJwt) throw new Error("Activation token was not returned.");

    const otpResponse = await userController.sendOtp({
      email: nextPayload.email,
      otpEmailSubject: draft.otpEmailSubject || OTP_EMAIL_SUBJECT,
    });
    if (otpResponse?.status !== 200) throw otpResponse;

    navigateWithDraft(
      mode === "delivery"
        ? "/register/delivery/verify"
        : "/register/vendor/verify",
      {
        registrationDTO: nextPayload,
        activationJwt,
      },
    );
  };

  const handleCompanyRegistration = async () => {
    if (!userService.getJwt())
      throw new Error("Missing session token. Please sign in again.");

    const companies = (draft.companyRegistrationDraft?.companies || []).map(
      (company) => ({
        ...company,
        customObject: (company.customObject || []).map((document) => ({
          ...cleanDocument(document),
          registrationNumber:
            mode === "delivery"
              ? document.registrationNumber || company.registrationNumber || "1"
              : null,
        })),
        termsAndConditionAccepted: true,
        userType:
          String(
            company.userType || registrationDTO.userType || "",
          ).toUpperCase() === "INDIVIDUAL_DRIVER"
            ? "INDIVIDUAL_DRIVER"
            : company.userType || registrationDTO.userType,
      }),
    );
    const missingDocuments =
      !companies.length ||
      companies.some(
        (company) =>
          !(company.customObject || []).length ||
          (company.customObject || []).some((document) => !document.data),
      );

    if (missingDocuments) {
      throw new Error(
        "Uploaded documents are no longer available. Please go back and upload them again.",
      );
    }

    const response = await companyController.CompanyRegistration({
      companyRegisterRequest: { companies },
      Authorization: userService.getJwt(),
    });
    if (isApiError(response) || !isSuccessResponse(response)) throw response;

    const responseData =
      response?.data?.data ?? response?.data?.result ?? response?.data;
    const companyId = extractCompanyId(responseData);
    userService.saveUserData({ Compid: companyId, BPID: "", BType: "" });
    clearRegistrationDraft();
    // Do not retain registration passwords/documents or return to the submit
    // screen when retrying session setup on the next step.
    navigate(
      mode === "delivery" ? "/register/delivery/address" : "/register/address",
      { replace: true },
    );
  };

  const handleContinue = async () => {
    if (!checked || loading) return;
    setLoading(true);
    try {
      if (documents) {
        await handleCompanyRegistration();
      } else {
        await handleRegisterUser();
      }
    } catch (error) {
      toast.error(
        getErrorMessage(
          error,
          documents ? "Company registration failed." : "Registration failed.",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <RegistrationLayout
      title={translateLabel("txt_terms_conditions", "Terms and Conditions")}
      icon={<TermsAndConditionsIcon />}
    >
      <div className="registration-terms-box">
        {isVendorTerms && (
          <>
            <h2>
              {translateLabel(
                "txt_common_d2i2a_w_noss_vendor_terms_conditions_privacy_policy",
                "D2I2A W NOSS Vendor Terms, Conditions & Privacy Policy",
              )}
            </h2>
            <p className="registration-terms-updated">
              {translateLabel(
                "txt_web_vendor_terms_last_updated",
                "Last Updated: June 2026",
              )}
            </p>
            <p>
              {" "}
              {translateLabel(
                "txt_common_welcome_to_d2i2a_w_noss_a_marketplace_platform_operated_and_owned_by_javelynx_thes",
                "Welcome to D2I2A W NOSS, a marketplace platform operated and owned by Javelynx. These Vendor Terms govern the relationship between D2I2A W NOSS and all vendors, merchants, restaurants, grocery stores, cosmetics, and other businesses operating on the Platform.",
              )}{" "}
            </p>
            <p>
              {" "}
              {translateLabel(
                "txt_common_by_registering_listing_products_or_using_the_vendor_platform_you_agree_to_these_te",
                "By registering, listing products, or using the Vendor platform, you agree to these Terms. If you do not agree, you may not use the Vendor services. D2I2A W NOSS reserves the right to update these Terms at any time.",
              )}{" "}
            </p>
          </>
        )}
        {termsSections.map((section) => (
          <section className="registration-terms-section" key={section.title}>
            <h3>{translateText(section.title)}</h3>
            {section.body.map((paragraph) => (
              <p key={paragraph}>{translateText(paragraph)}</p>
            ))}
          </section>
        ))}
      </div>
      <label className="registration-consent">
        <input
          type="checkbox"
          checked={checked}
          onChange={(event) => setChecked(event.target.checked)}
        />
        <span>
          {translateLabel(
            "input_agree_terms_conditions",
            "I agree with the terms and conditions",
          )}
        </span>
      </label>
      <button
        type="button"
        className="registration-primary"
        disabled={!checked || loading}
        onClick={handleContinue}
      >
        {loading
          ? documents
            ? translateLabel("btn_submitting", "Submitting...")
            : translateLabel("txt_common_please_wait", "Please wait...")
          : translateLabel("btn_accept", "Accept")}
      </button>
    </RegistrationLayout>
  );
};

export const VendorTerms = () => {
  useTranslationSnapshot();
  return <TermsStep mode="vendor" />;
};
export const DeliveryTerms = () => {
  useTranslationSnapshot();
  return <TermsStep mode="delivery" />;
};
export const DocumentTerms = () => {
  useTranslationSnapshot();
  const draft = useDraft();
  return (
    <TermsStep
      mode={draft.flow === "delivery" ? "delivery" : "vendor"}
      documents
    />
  );
};

const VerifyStep = ({ mode }) => {
  useTranslationSnapshot();
  const draft = useDraft();
  const navigateWithDraft = useDraftNavigate();
  const registrationDTO = draft.registrationDTO || {};
  const email = String(registrationDTO.email || "")
    .trim()
    .toLowerCase();
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [secondsLeft, setSecondsLeft] = useState(RESEND_INTERVAL);
  const [attemptsLeft, setAttemptsLeft] = useState(MAX_RESEND_ATTEMPTS);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");
  const inputsRef = useRef([]);
  const otpCode = otp.join("");
  const isComplete = otpCode.length === OTP_LENGTH && !otp.includes("");
  const canResend = attemptsLeft > 0 && secondsLeft === 0 && !resending;

  useEffect(() => {
    if (attemptsLeft <= 0 || secondsLeft <= 0) return undefined;
    const timer = setInterval(() => {
      setSecondsLeft((value) => Math.max(value - 1, 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [attemptsLeft, secondsLeft]);

  if (!email || !draft.activationJwt) {
    return (
      <Navigate
        to={
          mode === "delivery"
            ? "/register/delivery/account"
            : "/register/vendor/account"
        }
        replace
      />
    );
  }

  const updateOtp = (index, value) => {
    const digits = value.replace(/\D/g, "");
    const next = [...otp];

    if (digits.length > 1) {
      for (let i = 0; i < OTP_LENGTH; i += 1) next[i] = digits[i] || "";
      setOtp(next);
      inputsRef.current[Math.min(digits.length, OTP_LENGTH) - 1]?.focus();
      return;
    }

    next[index] = digits.slice(-1);
    setOtp(next);
    setError("");
    if (digits && index < OTP_LENGTH - 1) inputsRef.current[index + 1]?.focus();
  };

  const handleOtpPaste = (event) => {
    const pastedDigits = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);

    if (!pastedDigits) return;

    event.preventDefault();
    const next = Array(OTP_LENGTH).fill("");

    pastedDigits.split("").forEach((digit, offset) => {
      next[offset] = digit;
    });

    setOtp(next);
    setError("");
    inputsRef.current[Math.min(pastedDigits.length, OTP_LENGTH) - 1]?.focus();
  };

  const handleResend = async () => {
    if (!canResend) return;
    setResending(true);
    setError("");
    try {
      const response = await userController.sendOtp({
        email,
        otpEmailSubject: draft.otpEmailSubject || OTP_EMAIL_SUBJECT,
      });
      if (response?.status !== 200) throw response;
      setOtp(Array(OTP_LENGTH).fill(""));
      setSecondsLeft(RESEND_INTERVAL);
      setAttemptsLeft((value) => Math.max(value - 1, 0));
      toast.success(
        translateLabel("txt_end_user_otp_has_been_sent", "OTP has been sent."),
      );
    } catch (apiError) {
      setSecondsLeft(0);
      setError(getErrorMessage(apiError, "Failed to resend OTP."));
    } finally {
      setResending(false);
    }
  };

  const handleContinue = async () => {
    if (!isComplete || loading) return;
    setLoading(true);
    setError("");
    try {
      const verifyResponse = await userController.verifyEmailOtp({
        verifyOtpDTO: { email, otp: otpCode },
      });
      const verifyData = verifyResponse?.data;
      const verifyMessage = String(
        verifyData?.message || verifyData?.error || verifyData || "",
      ).toLowerCase();
      const verified =
        isSuccessResponse(verifyResponse) &&
        (verifyData === true ||
          verifyData?.verified === true ||
          verifyMessage.includes("verified") ||
          verifyMessage.includes("valid") ||
          (verifyResponse?.status === 204 && !verifyData));

      if (!verified) throw new Error("Invalid OTP.");

      const activateResponse = await userController.activateUser({
        email,
        jwt: draft.activationJwt,
      });
      const activated =
        isSuccessResponse(activateResponse) ||
        activateResponse?.data === true ||
        activateResponse?.data?.activated === true ||
        String(activateResponse?.data?.message || "")
          .toLowerCase()
          .includes("activated");
      if (!activated) throw new Error("Activation failed. Please try again.");

      const activationJwt =
        userService.getTokenFromResponse(activateResponse) ||
        draft.activationJwt;
      if (activationJwt) userService.saveJwt(activationJwt);

      const authResponse = await userController.authenticate({
        authenticateRequest: {
          username: email,
          password: registrationDTO.password,
        },
        authenticationPortalType: "ADMIN_PORTAL",
      });
      const freshJwt = userService.getTokenFromResponse(authResponse);
      if (authResponse?.status === 200 && freshJwt)
        userService.saveJwt(freshJwt);

      const payload = userService.getUserPayload() || {};
      const userId =
        payload.id ||
        extractUserId(activateResponse?.data, payload) ||
        registrationDTO.userID;

      initializeRegistrationSession(userId);

      try {
        const businessProfileResponse =
          await businessProfileController.getBusinessProfileTypesAndCategories({
            jwt: userService.getJwt(),
          });
      } catch (businessProfileError) {
        console.log("[registration] business profile API error", {
          status: businessProfileError?.response?.status,
          data: businessProfileError?.response?.data,
          message: businessProfileError?.message,
        });
      }

      navigateWithDraft(
        mode === "delivery"
          ? "/register/delivery/type"
          : "/register/vendor/documents",
        {
          registrationDTO: {
            ...registrationDTO,
            userID: userId,
            userId,
          },
        },
      );
    } catch (apiError) {
      setOtp(Array(OTP_LENGTH).fill(""));
      setError(getErrorMessage(apiError, "Verification failed."));
      inputsRef.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const formattedTime = `${Math.floor(secondsLeft / 60)
    .toString()
    .padStart(2, "0")}:${(secondsLeft % 60).toString().padStart(2, "0")}`;

  return (
    <RegistrationLayout
      title={translateLabel("txt_verification_code", "Verification Code")}
      icon={<RegistrationVerfIcon />}
    >
      <p className="registration-centered-copy">
        {translateLabel(
          "txt_web_vendor_verification_sent_prefix",
          "We have sent a verification code to",
        )}{" "}
        <strong>{email}</strong>
      </p>
      <div className="registration-otp">
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(input) => {
              inputsRef.current[index] = input;
            }}
            value={digit}
            inputMode="numeric"
            maxLength={1}
            onChange={(event) => updateOtp(index, event.target.value)}
            onPaste={handleOtpPaste}
            onKeyDown={(event) => {
              if (event.key === "Backspace" && !otp[index] && index > 0) {
                inputsRef.current[index - 1]?.focus();
              }
            }}
            aria-label={`OTP digit ${index + 1}`}
          />
        ))}
      </div>
      <div className="registration-resend">
        <span>
          {translateLabel("txt_code_not_received", "Didn't receive the code?")}
        </span>
        {attemptsLeft <= 0 ? (
          <strong>
            {translateLabel("txt_end_user_resend_disabled", "Resend disabled")}
          </strong>
        ) : canResend ? (
          <button type="button" onClick={handleResend}>
            {resending
              ? translateLabel("txt_end_user_resending", "Resending...")
              : `Resend (${attemptsLeft}/${MAX_RESEND_ATTEMPTS})`}
          </button>
        ) : (
          <strong>
            {translateLabel("txt_end_user_resend_in", "Resend in")}{" "}
            {formattedTime}
          </strong>
        )}
      </div>
      {error ? (
        <div className="registration-error">{translateText(error)}</div>
      ) : null}
      <button
        type="button"
        className="registration-primary is-narrow"
        disabled={loading || !isComplete}
        onClick={handleContinue}
      >
        {loading
          ? translateLabel("txt_end_user_verifying", "Verifying...")
          : translateLabel("btn_continue", "Continue")}
      </button>
    </RegistrationLayout>
  );
};

export const VendorVerify = () => {
  useTranslationSnapshot();
  return <VerifyStep mode="vendor" />;
};
export const DeliveryVerify = () => {
  useTranslationSnapshot();
  return <VerifyStep mode="delivery" />;
};

export const DeliveryType = () => {
  useTranslationSnapshot();
  const draft = useDraft();
  const navigateWithDraft = useDraftNavigate();
  const [selectedType, setSelectedType] = useState("");

  if (!draft.registrationDTO?.email)
    return <Navigate to="/register/delivery/account" replace />;

  const handleContinue = () => {
    if (!selectedType) {
      toast.error(
        translateLabel(
          "txt_common_please_select_user_type",
          "Please select user type.",
        ),
      );
      return;
    }
    const businessProfileID = String(
      draft.registrationDTO.businessProfileID ||
        draft.registrationDTO.businessProfileId ||
        draft.registrationDTO.businessProfileTypeId ||
        "",
    ).trim();
    navigateWithDraft("/register/delivery/documents", {
      registrationDTO: {
        ...draft.registrationDTO,
        businessProfileID,
        businessProfileId: businessProfileID,
        userType: selectedType,
      },
    });
  };

  return (
    <RegistrationLayout
      title={translateLabel("txt_create_account", "Create your Account")}
      icon={<RegistrationFormIcon />}
    >
      <label className="registration-field">
        <span>
          {" "}
          {translateFieldLabel("txt_common_user_type", "User Type")}
          <i>*</i>
        </span>
        <RegistrationDropdown
          value={selectedType}
          onChange={setSelectedType}
          options={DELIVERY_TYPE_OPTIONS}
          placeholder={translateLabel(
            "txt_common_select_your_user_type",
            "Select your user type",
          )}
        />
      </label>
      <button
        type="button"
        className="registration-primary"
        onClick={handleContinue}
      >
        {" "}
        {translateLabel("btn_save_continue", "Save & continue")}{" "}
      </button>
      <FooterTerms />
    </RegistrationLayout>
  );
};

const DocumentsStep = ({ mode }) => {
  useTranslationSnapshot();
  const draft = useDraft();
  const navigateWithDraft = useDraftNavigate();
  const isDelivery = mode === "delivery";
  const registrationDTO = draft.registrationDTO || {};
  const [name, setName] = useState(
    draft.companyRegistrationDraft?.companies?.[0]?.companyName ||
      registrationDTO.businessName ||
      [registrationDTO.firstname, registrationDTO.lastname]
        .filter(Boolean)
        .join(" ") ||
      "",
  );
  const [taxNumber, setTaxNumber] = useState(
    draft.companyRegistrationDraft?.companies?.[0]?.taxNumber || "",
  );
  const [taxNumberError, setTaxNumberError] = useState("");
  const [documents, setDocuments] = useState({});
  const [loadingField, setLoadingField] = useState("");

  if (!registrationDTO.email) {
    return (
      <Navigate
        to={
          isDelivery ? "/register/delivery/account" : "/register/vendor/account"
        }
        replace
      />
    );
  }

  const documentFields = isDelivery
    ? [
        {
          key: "idDocument",
          label: "Driver's ID / Passport",
          type: "ID",
          objectName: "driver-id-passport",
        },
        {
          key: "licenseDocument",
          label: "Driving License",
          type: "PROOF_OF_LEAGALITY",
          objectName: "driving-license",
        },
        {
          key: "vehicleDocument",
          label: "Vehicle Registration Paper",
          type: "PROOF_OF_LEAGALITY",
          objectName: "vehicle-registration-paper",
        },
      ]
    : [
        {
          key: "ownerDocument",
          label: "Owner ID / Passport",
          type: "ID",
          objectName: "owner-id-passport",
        },
        {
          key: "legalEntityDocument",
          label: "Proof of Legal Entity",
          type: "PROOF_OF_LEAGALITY",
          objectName: "proof-of-legal-entity",
        },
      ];

  const handleFile = async (field, file) => {
    setLoadingField(field.key);
    try {
      const document = await fileToPickedDocument(file, {
        attachmentType: field.type,
        objectName: field.objectName,
        countryName: registrationDTO.countryOfResidence || "Lebanon",
        registrationNumber: isDelivery ? "1" : null,
        userName: registrationDTO.userName || registrationDTO.email,
        label: field.label,
      });
      setDocuments((current) => ({ ...current, [field.key]: document }));
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to select file."));
    } finally {
      setLoadingField("");
    }
  };

  const handleContinue = () => {
    const trimmedName = name.trim();
    const trimmedTaxNumber = isDelivery ? "1" : taxNumber.trim();
    if (!trimmedName) {
      toast.error(
        isDelivery
          ? REGISTRATION_VALIDATION_MESSAGES.driverNameRequired
          : REGISTRATION_VALIDATION_MESSAGES.companyNameRequired,
      );
      return;
    }
    if (!isDelivery && taxNumberError) {
      toast.error(taxNumberError);
      return;
    }
    if (documentFields.some((field) => !documents[field.key])) {
      toast.error(
        isDelivery
          ? REGISTRATION_VALIDATION_MESSAGES.driverDocumentsRequired
          : REGISTRATION_VALIDATION_MESSAGES.companyDocumentsRequired,
      );
      return;
    }

    const businessProfileID = String(
      registrationDTO.businessProfileID ||
        registrationDTO.businessProfileId ||
        registrationDTO.businessProfileTypeId ||
        registrationDTO.businessProfileCategoryId ||
        "",
    ).trim();
    const customObject = documentFields.map((field) =>
      cleanDocument(documents[field.key]),
    );

    const companyPayload = {
      ...(businessProfileID ? { businessProfileID } : {}),
      businessDescription: registrationDTO.businessDescription || "",
      companyName: trimmedName,
      companyType: "PRIMARY",
      cuisineType: isDelivery ? "" : registrationDTO.cuisineType || "",
      customObject: isDelivery
        ? customObject.map((document) => ({
            ...document,
            registrationNumber: trimmedTaxNumber,
          }))
        : customObject,
      registrationNumber: trimmedTaxNumber,
      taxNumber: trimmedTaxNumber,
      termsAndConditionAccepted: false,
      userType: isDelivery
        ? registrationDTO.userType || "INDIVIDUAL_DRIVER"
        : "VENDOR",
    };

    navigateWithDraft("/register/documents/terms", {
      flow: isDelivery ? "delivery" : "vendor",
      companyRegistrationDraft: {
        companies: [companyPayload],
      },
    });
  };

  return (
    <RegistrationLayout
      title={translateLabel("txt_create_account", "Create your Account")}
      icon={<RegistrationFormIcon />}
      className="registration-documents-page"
    >
      <div className="registration-form">
        <TextField
          label={translateText(isDelivery ? "Driver Name" : "Company Name")}
          icon={<UserProfileIcon size={21.78} />}
        >
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </TextField>
        {documentFields.map((field) => (
          <label key={field.key} className="registration-upload">
            <span>
              {cleanRequiredLabel(translateText(field.label))}
              <i>*</i>
            </span>
            <input
              type="file"
              accept="image/*,application/pdf"
              onChange={(event) => handleFile(field, event.target.files?.[0])}
            />
            <div>
              <UploadIcon size={22} />
              <strong>
                {loadingField === field.key
                  ? translateLabel("txt_common_reading_file", "Reading file...")
                  : documents[field.key]?.label ||
                    translateLabel("txt_common_choose_file", "Choose file")}
              </strong>
              {documents[field.key]?.previewUri ? (
                <img src={documents[field.key].previewUri} alt="" />
              ) : null}
            </div>
          </label>
        ))}
        {!isDelivery ? (
          <TextField
            label={translateLabel("input_tax_number", "TAX Number")}
            icon={<LegalEntityIcon size={22} />}
            required={false}
            error={taxNumberError}
          >
            <input
              type="text"
              inputMode="numeric"
              value={taxNumber}
              onChange={(event) => {
                const rawValue = event.target.value;
                const value = rawValue.replace(/\D/g, "");
                setTaxNumber(value);
                setTaxNumberError(
                  rawValue === value
                    ? ""
                    : REGISTRATION_VALIDATION_MESSAGES.taxNumberInvalid,
                );
              }}
            />
          </TextField>
        ) : null}
        <button
          type="button"
          className="registration-primary"
          onClick={handleContinue}
        >
          {" "}
          {translateLabel("btn_create_account", "Create Account")}{" "}
        </button>
      </div>
      <FooterTerms />
    </RegistrationLayout>
  );
};

export const VendorDocuments = () => {
  useTranslationSnapshot();
  return <DocumentsStep mode="vendor" />;
};
export const DeliveryDocuments = () => {
  useTranslationSnapshot();
  return <DocumentsStep mode="delivery" />;
};

export const RegistrationSuccess = ({ mode = "vendor" }) => {
  useTranslationSnapshot();
  const navigate = useNavigate();
  const isDelivery = mode === "delivery";

  return (
    <RegistrationLayout
      title={translateLabel(
        "txt_common_verification_in_progress",
        "Verification in Progress",
      )}
      icon={<SuccessRegistrationIcon />}
      className="registration-success-page"
    >
      <p className="registration-centered-copy">
        {translateLabel(
          "txt_web_vendor_documents_under_review",
          "We are currently reviewing your legal documents, tax number, and ID. Until your information is fully verified, your access will remain limited as a guest.",
        )}
      </p>
      <p className="registration-centered-copy">
        <strong>
          {" "}
          {translateLabel(
            "txt_common_we_will_notify_you_once_your_account_is_approved_and_activated",
            "We will notify you once your account is approved and activated.",
          )}{" "}
        </strong>
      </p>
      <button
        type="button"
        className="registration-primary is-narrow"
        onClick={() => navigate(isDelivery ? "/sign-in" : "/vendor-dashboard")}
      >
        {isDelivery
          ? translateLabel("txt_end_user_go_to_sign_in", "Go to Sign In")
          : translateLabel("txt_common_go_to_account", "Go to Account")}
      </button>
    </RegistrationLayout>
  );
};

export const DeliveryRegistrationSuccess = () => {
  useTranslationSnapshot();
  return <RegistrationSuccess mode="delivery" />;
};
export const VendorAccount = () => {
  useTranslationSnapshot();
  return <RegistrationAccountForm mode="vendor" />;
};
export const DeliveryAccount = () => {
  useTranslationSnapshot();
  return <RegistrationAccountForm mode="delivery" />;
};
